import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { createComponent } from 'react-fela'
import { isEmpty, isEqual } from 'lodash'

import RangeSlider from 'design-framework/atoms/RangeSlider/index'
import MinMaxSelect from 'design-framework/molecules/MinMaxSelect/index'
import RadioButton from 'design-framework/atoms/RadioButton/index'
import Typography from 'design-framework/atoms/Typography/index'

import { __freePriceFilterWrapper, __wrapper } from './__style'

const FreePriceFilterWrapper = createComponent(__freePriceFilterWrapper)
const Wrapper = createComponent(__wrapper)

const PriceFilter = ({ config = [], onChange = () => {}, filterCode = '' }) => {
	const { range: rangeConfig = {}, free: freeConfig = {} } = config.reduce(
		(agg, conf) => ({ ...agg, ...{ [conf.name]: conf } }),
		{}
	)

	const { values = {}, range = {}, areFreeTicketsAvailable } = rangeConfig

	const [isFreeActive, setIsFreeActive] = useState(freeConfig.isActive)

	useEffect(() => {
		setIsFreeActive(freeConfig.isActive)
	}, [freeConfig.isActive])

	const [value, setValue] = useState(values)
	const [prevValue, setPrevValue] = useState(values)

	const handleOnchange = updatedValue => {
		setValue(updatedValue)
		onChange(updatedValue, filterCode)
	}

	const handleToggleClick = val => {
		setIsFreeActive(val)
		onChange(freeConfig.name, filterCode)
	}

	if (isEqual(values, value) && !isEqual(values, prevValue)) {
		setPrevValue(value)
	}

	if (!isEqual(values, value) && !isEqual(values, prevValue)) {
		setValue(values)
		setPrevValue(values)
	}

	const isFreePriceFilterVisible =
		areFreeTicketsAvailable !== undefined
			? areFreeTicketsAvailable
			: !isEmpty(freeConfig)

	return (
		<Wrapper>
			{isFreePriceFilterVisible && (
				<FreePriceFilterWrapper>
					<Typography
						variant="body2"
						color="grey5"
						styles={{
							maxWidth: 154,
						}}
					>
						Only show events which have{' '}
						<strong>free tickets</strong>
					</Typography>
					<RadioButton
						isOn={freeConfig.isActive}
						onToggle={handleToggleClick}
						offColor="darkGrey5"
					/>
				</FreePriceFilterWrapper>
			)}
			{!isEmpty(rangeConfig) && (
				<React.Fragment>
					<RangeSlider
						values={value}
						range={range}
						onChange={handleOnchange}
						unsetMarginSide
						disabled={isFreeActive}
						step={100}
					/>
					<MinMaxSelect
						range={range}
						values={value}
						onChange={handleOnchange}
						disabled={isFreeActive}
						step={100}
					/>
				</React.Fragment>
			)}
		</Wrapper>
	)
}

PriceFilter.propTypes = {
	/** Configs for the price filter  */
	config: PropTypes.arrayOf(
		PropTypes.shape({
			groupName: PropTypes.string,
			isActive: PropTypes.bool,
			name: PropTypes.oneOf(['free', 'range']),
			areFreeTicketsAvailable: PropTypes.bool,
			code: PropTypes.oneOf(['free', 'range']),
			criteria: PropTypes.string,
			/** Min and Max Range of the slider */
			range: PropTypes.shape({
				min: PropTypes.number,
				max: PropTypes.number,
			}),
			/** Min and Max Selected Values of the slider  */
			values: PropTypes.shape({
				min: PropTypes.number,
				max: PropTypes.number,
			}),
		})
	),
	/** Callback to call when slider values are changed. */
	onChange: PropTypes.func,
	/** Add filtercode to the onChange function */
	filterCode: PropTypes.string,
}

export default React.memo(PriceFilter)
